import { useEffect, useRef, useState } from "react";
import type { DayviseClient, TranscriptFormat } from "@dayvise/client-sdk";
import { Button, Select, rowStyle } from "@dayvise/react-ui";
import { saveFileContent } from "./fileDownload.ts";

/** Downloads only on a click. Branch/account changes invalidate pending private bytes. */
export function TranscriptDownload({ client, scopeKey, conversationId, branchId }: {
  readonly client: Pick<DayviseClient, "conversations">;
  readonly scopeKey: string;
  readonly conversationId: string;
  readonly branchId: string | null;
}) {
  const identity = JSON.stringify([scopeKey, conversationId, branchId]);
  const current = useRef<string | null>(identity);
  const generation = useRef(0);
  const pending = useRef<AbortController | null>(null);
  if (current.current !== identity) { generation.current++; pending.current?.abort(); pending.current = null; }
  current.current = identity;
  useEffect(() => {
    current.current = identity;
    return () => { current.current = null; generation.current++; pending.current?.abort(); pending.current = null; };
  }, [identity, client]);
  const [format, setFormat] = useState<TranscriptFormat>("json");
  const [redact, setRedact] = useState(true);
  const [state, setState] = useState<{ identity: string; busy: boolean; error: string | null }>({ identity, busy: false, error: null });
  const busy = state.identity === identity && state.busy;
  const download = async () => {
    if (!branchId || pending.current) return;
    const abort = new AbortController();
    pending.current = abort;
    const epoch = generation.current;
    const active = () => current.current === identity && generation.current === epoch && !abort.signal.aborted;
    setState({ identity, busy: true, error: null });
    try {
      const file = await client.conversations.downloadTranscript(conversationId, { branchId, format, redact, signal: abort.signal });
      if (active()) { saveFileContent(file); setState({ identity, busy: false, error: null }); }
    } catch (error) {
      if (active()) setState({ identity, busy: false, error: error instanceof Error ? error.message : "Transcript download failed." });
    } finally {
      if (pending.current === abort) pending.current = null;
    }
  };
  return <div>
    <div style={rowStyle("var(--dayvise-space-sm)")}>
      <Select label="Transcript format" value={format} disabled={busy}
        options={[{ value: "json", label: "JSON" }, { value: "md", label: "Markdown" }, { value: "html", label: "HTML" }]}
        onChange={(event) => setFormat(event.target.value as TranscriptFormat)} />
      <label><input type="checkbox" checked={redact} disabled={busy} onChange={(event) => setRedact(event.target.checked)} /> Redact recognized credentials</label>
      <Button variant="secondary" disabled={!branchId || busy} onClick={() => void download()}>{busy ? "Downloading transcript…" : "Download transcript"}</Button>
    </div>
    {state.identity === identity && state.error ? <span role="alert">{state.error}</span> : null}
  </div>;
}
